import {useEffect, useRef, useState} from "react"

type LazyLoadOptions = {
   active?: boolean
   loadImmediately?: boolean
   rootMargin?: string
   resetKey?: unknown
   observeKey?: unknown
}

export const useLazyLoadOnView = <T extends HTMLElement>({
   active = true,
   loadImmediately = false,
   rootMargin = "0px",
   resetKey,
   observeKey,
}: LazyLoadOptions = {}) => {
   const ref = useRef<T | null>(null)
   const [shouldLoad, setShouldLoad] = useState(loadImmediately)

   useEffect(() => {
      setShouldLoad(loadImmediately)
   }, [loadImmediately, resetKey])

   useEffect(() => {
      if (!active || shouldLoad) return

      const element = ref.current
      if (!element) return

      if (typeof IntersectionObserver === "undefined") {
         setShouldLoad(true)
         return
      }

      const observer = new IntersectionObserver(
         ([entry]) => {
            if (entry.isIntersecting) {
               setShouldLoad(true)
               observer.disconnect()
            }
         },
         {rootMargin},
      )

      observer.observe(element)

      return () => {
         observer.disconnect()
      }
   }, [active, observeKey, rootMargin, shouldLoad])

   return {ref, shouldLoad}
}
